import * as React from 'react';
import { cn } from '@/lib/utils/cn';

/**
 * One report page on screen (T-0283; E24-design §9): the card the
 * carousel gives it, drawn with the PDF's margins and reading order. The
 * eyebrow names the page as the PDF's page label does ("Page 2 of 11 ·
 * Executive summary"); a code page draws its own headline here, an AI
 * page's headline is its first block in the editor.
 */
export function PageFrame({
  index,
  count,
  eyebrow,
  title,
  children,
  className,
}: {
  index: number;
  count: number;
  eyebrow: string;
  /** A code-written headline; omitted when the page's headline is an editable block. */
  title?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex min-h-full flex-col gap-4 px-5 py-5 md:px-8 md:py-7', className)}>
      <p className="m-0 text-meta font-semibold uppercase tracking-wider text-ink-500">
        Page {index + 1} of {count} · {eyebrow}
      </p>
      {title ? (
        <h2 className="m-0 font-serif text-h1 font-semibold leading-tight text-ink-900">{title}</h2>
      ) : null}
      {children}
    </div>
  );
}

/** A heading inside a page, as the PDF's h3 ("Why these", "Watch-outs"). */
export function PageHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="m-0 text-h3 font-semibold text-ink-900">{children}</h3>;
}

/** The PDF's `.vi-lede`: a paragraph under the headline, written by code. */
export function PageLede({ children }: { children: React.ReactNode }) {
  return <p className="m-0 text-body text-ink-700">{children}</p>;
}

/** The PDF's `.note`: a line of small print under a table. */
export function PageNote({ children }: { children: React.ReactNode }) {
  return <p className="m-0 text-meta text-ink-500">{children}</p>;
}

/** What a code page says when nothing was frozen for it. */
export function PageMissing({ children }: { children: React.ReactNode }) {
  return (
    <p className="m-0 rounded-card border border-dashed border-ink-200 px-4 py-3 text-body-sm text-ink-500">
      {children}
    </p>
  );
}

/** The shared table shell: the fit summary's look (T-0267). */
export function PageTable({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('overflow-x-auto rounded-card border border-ink-100', className)}>
      <table className="w-full border-collapse text-body-sm" aria-label={label}>
        {children}
      </table>
    </div>
  );
}

export const TH = 'px-3 py-2 text-left text-meta font-medium text-ink-500';
export const TH_RIGHT = 'px-3 py-2 text-right text-meta font-medium text-ink-500';
export const TD = 'px-3 py-2 text-left align-top text-ink-900';
export const TD_NUM = 'px-3 py-2 text-right font-mono tabular-nums text-ink-700';
